import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';
import { Alert, Input, Modal, Pagination, Select } from 'antd';
import { Button, SearchableSelect } from '@/shared/components';
import type { SearchableOption } from '@/shared/components';
import { budgetsApi } from '@/features/budgets/api';
import { useDefaultYear } from '@/features/settings/hooks/useDashboardSettingsQuery';
import { ClassificationWorkflowRequiredAlert } from '@/features/reports/components/ClassificationWorkflowRequiredAlert';
import { FollowUpsEditor } from '@/features/reports/components/FollowUpsEditor';
import type { RiskTriageIndicator, RiskTriageReviewRow } from '@/features/reports/api';
import {
  riskTriageReportErrorMessage,
  useRiskTriageReportQuery,
} from '@/features/reports/hooks/useRiskTriageReportQuery';
import { useSchoolBandsConfiguredForYear } from '@/features/reports/hooks/useSchoolBandsConfiguredForYear';
import {
  DashboardSettingSourceNotes,
} from '@/features/settings/components/DashboardSettingSourceNote';
import {
  useRiskTriageAnomalyHighThresholdResolved,
  useRiskTriageAnomalyMediumThresholdResolved,
  useRiskTriageVarianceHighlightThresholdResolved,
} from '@/features/settings/hooks/useResolvedDashboardSetting';

const PAGE_SIZE_OPTIONS = [10, 15, 25, 50] as const;

const MIN_REPORT_YEAR = 1990;

const ANOMALY_SCORE_EXPLANATION =
  "The Anomaly Score is a weighted measure (0–100) representing a school's deviation from its assigned regulatory bands and peer financial medians. Points are assigned based on Tuition Band Compliance (40%), Teacher Salary Benchmarks (40%), and School Size Alignment (20%).";

function formatVariancePct(p: number): string {
  if (!Number.isFinite(p)) return '—';
  const sign = p > 0 ? '+' : '';
  return `${sign}${p.toFixed(1)}%`;
}

/** Local validation for reporting year before calling the risk triage API. */
function validateReportYear(yearStr: string): string | null {
  const t = yearStr.trim();
  if (!t) return 'Select a reporting year.';
  if (!/^\d+$/.test(t)) return 'Year must contain digits only.';
  const n = parseInt(t, 10);
  if (!Number.isFinite(n)) return 'Year is not valid.';
  const maxY = new Date().getFullYear() + 1;
  if (n < MIN_REPORT_YEAR || n > maxY) {
    return `Year must be between ${MIN_REPORT_YEAR} and ${maxY}.`;
  }
  return null;
}

function AnomalyScoreCell({
  score,
  highThreshold,
  mediumThreshold,
}: {
  score: number;
  highThreshold: number;
  mediumThreshold: number;
}) {
  const s = Number.isFinite(score) ? score : 0;
  if (s >= highThreshold) {
    return (
      <span className="inline-flex min-w-[2.75rem] justify-center rounded-md bg-red-700 px-2 py-0.5 text-center text-xs font-black tabular-nums text-white ring-1 ring-red-900/30">
        {s.toFixed(0)}
      </span>
    );
  }
  if (s >= mediumThreshold) {
    return (
      <span className="inline-flex min-w-[2.75rem] justify-center rounded-md bg-amber-500 px-2 py-0.5 text-center text-xs font-bold tabular-nums text-amber-950 ring-1 ring-amber-700/25">
        {s.toFixed(0)}
      </span>
    );
  }
  return (
    <span className="inline-flex min-w-[2.75rem] justify-center rounded-md bg-slate-200 px-2 py-0.5 text-center text-xs font-semibold tabular-nums text-slate-800 ring-1 ring-slate-300">
      {s.toFixed(0)}
    </span>
  );
}

function IndicatorDetailTable({
  indicators,
  varianceHighlightThreshold,
}: {
  indicators: RiskTriageIndicator[];
  varianceHighlightThreshold: number;
}) {
  if (!indicators.length) {
    return <p className="text-xs text-slate-500">No indicator-level flags returned for this school.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full border-collapse text-left text-xs">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-100">
            <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-700">Indicator</th>
            <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-700">School value</th>
            <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-700">Peer median</th>
            <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-700">Variance</th>
            <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-700"> </th>
          </tr>
        </thead>
        <tbody>
          {indicators.map((ind, idx) => (
            <tr key={`${ind.name}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/80'}>
              <td className="border-b border-slate-100 px-2 py-1.5 font-semibold text-slate-900">{ind.name}</td>
              <td className="border-b border-slate-100 px-2 py-1.5 font-mono tabular-nums text-slate-800">
                {ind.schoolValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </td>
              <td className="border-b border-slate-100 px-2 py-1.5 font-mono tabular-nums text-slate-600">
                {ind.peerMedian.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </td>
              <td className="border-b border-slate-100 px-2 py-1.5">
                <span
                  className={`font-mono text-sm font-black tabular-nums ${
                    Math.abs(ind.variancePercentage) >= varianceHighlightThreshold ? 'text-rose-700' : 'text-slate-800'
                  }`}
                >
                  {formatVariancePct(ind.variancePercentage)}
                </span>
              </td>
              <td className="border-b border-slate-100 px-2 py-1.5 text-center">
                {ind.isFlagged ? (
                  <span title="Flagged outlier" className="inline-flex text-red-600">
                    <AlertTriangle className="size-4" aria-label="Outlier" />
                  </span>
                ) : (
                  <span className="text-slate-300">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RiskTriageReportPage() {
  const apiDefaultYear = useDefaultYear();
  const anomalyHighResolved = useRiskTriageAnomalyHighThresholdResolved();
  const anomalyMediumResolved = useRiskTriageAnomalyMediumThresholdResolved();
  const varianceHighlightResolved = useRiskTriageVarianceHighlightThresholdResolved();
  const [year, setYear] = useState('');
  const [yearLabel, setYearLabel] = useState('');

  useEffect(() => {
    if (!year && apiDefaultYear) {
      setYear(apiDefaultYear);
      setYearLabel(apiDefaultYear);
    }
  }, [apiDefaultYear]);

  const yearValidationError = useMemo(() => validateReportYear(year), [year]);
  const queryYear =
    yearValidationError === null && /^\d+$/.test(year.trim()) ? parseInt(year.trim(), 10) : null;

  const yearStrForBands = queryYear != null ? String(queryYear) : undefined;
  const bandsGate = useSchoolBandsConfiguredForYear(yearStrForBands);
  const classificationWorkflowReady =
    queryYear == null ? undefined : bandsGate.isSuccess && bandsGate.configured ? true : false;

  const reportQuery = useRiskTriageReportQuery(queryYear, { classificationWorkflowReady });
  const reportDataUnlocked = queryYear != null && bandsGate.isSuccess && bandsGate.configured;
  const reportReady = reportDataUnlocked;
  const data = reportDataUnlocked ? reportQuery.data : undefined;
  const errMsg =
    reportDataUnlocked && reportQuery.isError ? riskTriageReportErrorMessage(reportQuery.error) : null;

  const [regionFilter, setRegionFilter] = useState<string>('__all__');
  const [peerFilter, setPeerFilter] = useState<string>('__all__');
  const [search, setSearch] = useState('');
  const [sortDesc, setSortDesc] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[1]);

  const [detailRow, setDetailRow] = useState<RiskTriageReviewRow | null>(null);
  const [actionRow, setActionRow] = useState<RiskTriageReviewRow | null>(null);

  const resetQueueAndFilters = useCallback(() => {
    setRegionFilter('__all__');
    setPeerFilter('__all__');
    setSearch('');
    setSortDesc(true);
    setPage(1);
    setDetailRow(null);
    setActionRow(null);
  }, []);

  const prevQueryYearRef = useRef<number | null>(null);
  useEffect(() => {
    if (queryYear === null) {
      resetQueueAndFilters();
      prevQueryYearRef.current = null;
      return;
    }
    if (prevQueryYearRef.current !== null && prevQueryYearRef.current !== queryYear) {
      resetQueueAndFilters();
    }
    prevQueryYearRef.current = queryYear;
  }, [queryYear, resetQueueAndFilters]);

  const fetchBudgetYears = useCallback(async (q: string): Promise<SearchableOption[]> => {
    const list = await budgetsApi.getBudgetYears({
      skipCount: 0,
      maxResultCount: 100,
      year: q || undefined,
    });
    return (list ?? []).map((y) => ({ id: String(y), label: String(y) }));
  }, []);

  const handleYearSelect = useCallback((id: string, label: string) => {
    setYear(id);
    setYearLabel(label);
  }, []);

  const regions = useMemo(() => {
    const set = new Set<string>();
    for (const r of data?.reviewQueue ?? []) {
      if (r.region?.trim()) set.add(r.region.trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data?.reviewQueue]);

  const peerGroups = useMemo(() => {
    const set = new Set<string>();
    for (const r of data?.reviewQueue ?? []) {
      if (r.peerGroup?.trim()) set.add(r.peerGroup.trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data?.reviewQueue]);

  const filteredRows = useMemo(() => {
    const rows = data?.reviewQueue ?? [];
    const q = search.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (regionFilter !== '__all__' && r.region !== regionFilter) return false;
      if (peerFilter !== '__all__' && r.peerGroup !== peerFilter) return false;
      if (q && !r.schoolName.toLowerCase().includes(q)) return false;
      return true;
    });
    out = [...out].sort((a, b) => (sortDesc ? b.anomalyScore - a.anomalyScore : a.anomalyScore - b.anomalyScore));
    return out;
  }, [data?.reviewQueue, regionFilter, peerFilter, search, sortDesc]);

  useEffect(() => {
    setPage(1);
  }, [regionFilter, peerFilter, search, sortDesc]);

  const totalFiltered = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const openAction = useCallback((row: RiskTriageReviewRow, e: MouseEvent) => {
    e.stopPropagation();
    setDetailRow(null);
    setActionRow(row);
  }, []);

  const closeAction = useCallback(() => setActionRow(null), []);

  return (
    <div className="font-[Inter,ui-sans-serif,system-ui,sans-serif] text-slate-900">
      <h1 className="mb-3 text-xl font-bold tracking-tight text-slate-900">Risk triage report</h1>
      {/* Governing header — Template 3 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-800 bg-slate-800 px-4 py-2 text-white">
        <div className="flex flex-wrap items-center gap-4 text-xs font-semibold uppercase tracking-wide">
          <span>
            Reporting year:{' '}
            <span className="rounded bg-white/15 px-2 py-0.5 font-mono text-sm normal-case tracking-normal text-white">
              {yearLabel || year || '—'}
            </span>
          </span>
          <span className="hidden h-4 w-px bg-white/25 sm:block" aria-hidden />
          <span className="normal-case">
            Global validation:{' '}
            <span className="rounded-md bg-white/10 px-2 py-0.5 font-bold text-white ring-1 ring-white/20">
              {!reportReady
                ? '—'
                : reportQuery.isLoading
                  ? 'Loading…'
                  : (data?.globalValidationStatus ?? '—')}
            </span>
          </span>
        </div>
        <p className="text-[10px] font-medium uppercase tracking-wider text-slate-300">PSB · Risk triage</p>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-[12rem] max-w-xs">
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">
            Report year
          </label>
          <div
            className={
              yearValidationError
                ? 'rounded-lg ring-2 ring-amber-500 ring-offset-1 ring-offset-white'
                : 'rounded-lg'
            }
          >
            <SearchableSelect
              value={year}
              onSelect={handleYearSelect}
              fetchOptions={fetchBudgetYears}
              placeholder="Year…"
              selectedLabel={yearLabel || year}
              clearable={false}
              className="w-full"
            />
          </div>
          {yearValidationError ? (
            <p className="mt-1.5 text-xs font-medium text-amber-900" role="alert">
              {yearValidationError}
            </p>
          ) : null}
        </div>
      </div>

      {errMsg ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{errMsg}</div>
      ) : null}

      {queryYear != null && bandsGate.isPending ? (
        <p className="mb-4 text-center text-sm text-slate-600">Checking classification setup for this year…</p>
      ) : null}

      {queryYear != null && bandsGate.isError ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Could not verify classification workflow"
          description={
            bandsGate.error instanceof Error ? bandsGate.error.message : 'Could not load school bands for this year.'
          }
        />
      ) : null}

      {queryYear != null && bandsGate.notConfigured ? (
        <ClassificationWorkflowRequiredAlert yearLabel={yearLabel || String(queryYear)} />
      ) : null}

      {reportDataUnlocked ? (
        <>
          {/* Summary KPIs */}
          <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-3 shadow-sm ring-1 ring-slate-100">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Total flagged schools</p>
          <p className="mt-1 text-2xl font-black tabular-nums text-slate-900">
            {!reportReady ? '—' : reportQuery.isLoading ? '…' : (data?.totalFlaggedSchools ?? 0).toLocaleString()}
          </p>
        </div>
        <div className="rounded-lg border-2 border-red-200 bg-red-50/50 px-3 py-3 shadow-sm ring-1 ring-red-100">
          <p className="text-[10px] font-bold uppercase tracking-wide text-red-900">Critical severity</p>
          <p className="mt-1 text-2xl font-black tabular-nums text-red-800">
            {!reportReady ? '—' : reportQuery.isLoading ? '…' : (data?.highSeverityCount ?? 0).toLocaleString()}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-3 shadow-sm ring-1 ring-slate-100">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Avg. data completeness</p>
          <p className="mt-1 text-2xl font-black tabular-nums text-slate-900">
            {!reportReady ? '—' : reportQuery.isLoading ? '…' : `${Math.round(data?.averageDataCompleteness ?? 0)}%`}
          </p>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-slate-700 transition-[width]"
              style={{
                width: `${!reportReady ? 0 : Math.min(100, Math.max(0, data?.averageDataCompleteness ?? 0))}%`,
              }}
            />
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-3 shadow-sm ring-1 ring-slate-100">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Review queue (rows)</p>
          <p className="mt-1 text-2xl font-black tabular-nums text-slate-900">
            {!reportReady ? '—' : reportQuery.isLoading ? '…' : (data?.reviewQueue.length ?? 0).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="mb-5 flex gap-3 rounded-lg border border-slate-200 bg-slate-50/90 px-3 py-3 shadow-sm ring-1 ring-slate-100">
        <Info className="mt-0.5 size-4 shrink-0 text-slate-500" aria-hidden />
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-600">About the anomaly score</p>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-800">{ANOMALY_SCORE_EXPLANATION}</p>
          <ul className="mt-2 list-none space-y-1 border-t border-slate-200/80 pt-2 text-xs text-slate-600">
            <li>
              <span className="font-semibold text-slate-700">Tuition band compliance</span> — 40%
            </li>
            <li>
              <span className="font-semibold text-slate-700">Teacher salary benchmarks</span> — 40%
            </li>
            <li>
              <span className="font-semibold text-slate-700">School size alignment</span> — 20%
            </li>
          </ul>
          <DashboardSettingSourceNotes
            className="mt-3 border-t border-slate-200/80 pt-2"
            resolved={[anomalyHighResolved, anomalyMediumResolved, varianceHighlightResolved]}
          />
        </div>
      </div>

      {/* Filters */}
      <div
        className={`mb-3 flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-3 sm:flex-row sm:flex-wrap sm:items-end ${!reportReady ? 'pointer-events-none opacity-50' : ''}`}
        aria-disabled={!reportReady}
      >
        <div className="min-w-[10rem] flex-1">
          <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">School name</label>
          <Input
            allowClear
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-sm"
          />
        </div>
        <div className="min-w-[10rem]">
          <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Region</label>
          <Select
            className="w-full min-w-[10rem]"
            value={regionFilter}
            onChange={setRegionFilter}
            options={[
              { value: '__all__', label: 'All regions' },
              ...regions.map((r) => ({ value: r, label: r })),
            ]}
          />
        </div>
        <div className="min-w-[10rem]">
          <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Peer group</label>
          <Select
            className="w-full min-w-[10rem]"
            value={peerFilter}
            onChange={setPeerFilter}
            options={[
              { value: '__all__', label: 'All peer groups' },
              ...peerGroups.map((p) => ({ value: p, label: p })),
            ]}
          />
        </div>
      </div>

      {/* Main table — key remounts body when reporting year changes */}
      <div
        key={queryYear ?? 'invalid-year'}
        className="overflow-hidden rounded-lg border border-slate-300 bg-white shadow-sm"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-slate-300 bg-slate-100">
                <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-800">School name</th>
                <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-800">Region</th>
                <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-800">Peer group</th>
                <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-800">
                  <button
                    type="button"
                    title="0–100 weighted score: tuition compliance 40%, teacher salary benchmarks 40%, school size alignment 20%."
                    className="inline-flex items-center gap-1 font-bold uppercase tracking-wide text-slate-800 hover:text-slate-950"
                    onClick={() => setSortDesc((d) => !d)}
                  >
                    Anomaly score
                    <span className="font-mono text-[10px] font-normal normal-case text-slate-500">
                      {sortDesc ? '↓' : '↑'}
                    </span>
                  </button>
                </th>
                <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-800">Primary anomaly</th>
                <th className="px-2 py-1.5 font-bold uppercase tracking-wide text-slate-800">Status</th>
                <th className="w-28 px-2 py-1.5 font-bold uppercase tracking-wide text-slate-800">Action</th>
              </tr>
            </thead>
            <tbody>
              {!reportReady ? (
                <tr>
                  <td colSpan={7} className="px-2 py-8 text-center text-sm font-medium text-amber-900">
                    {yearValidationError ?? 'Select a valid reporting year to load the review queue.'}
                  </td>
                </tr>
              ) : reportQuery.isLoading ? (
                <tr>
                  <td colSpan={7} className="px-2 py-8 text-center text-slate-500">
                    Loading review queue…
                  </td>
                </tr>
              ) : totalFiltered === 0 ? (
                <tr>
                  <td colSpan={7} className="px-2 py-8 text-center text-slate-500">
                    No rows match the current filters.
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, idx) => {
                  const selected = detailRow?.id === row.id;
                  const stripe = ((page - 1) * pageSize + idx) % 2 === 0;
                  return (
                    <tr
                      key={row.id}
                      className={`cursor-pointer border-b border-slate-200 transition-colors hover:bg-slate-50 ${
                        selected ? 'bg-sky-50/80' : stripe ? 'bg-white' : 'bg-slate-50/40'
                      }`}
                      onClick={() => setDetailRow((cur) => (cur?.id === row.id ? null : row))}
                    >
                      <td className="max-w-[220px] truncate px-2 py-1 font-semibold text-slate-900">{row.schoolName}</td>
                      <td className="whitespace-nowrap px-2 py-1 text-slate-700">{row.region}</td>
                      <td className="max-w-[160px] truncate px-2 py-1 text-slate-700">{row.peerGroup}</td>
                      <td className="whitespace-nowrap px-2 py-1">
                        <AnomalyScoreCell
                          score={row.anomalyScore}
                          highThreshold={anomalyHighResolved.value}
                          mediumThreshold={anomalyMediumResolved.value}
                        />
                      </td>
                      <td className="max-w-[200px] truncate px-2 py-1 text-slate-800">{row.primaryAnomaly}</td>
                      <td className="whitespace-nowrap px-2 py-1 text-slate-700">{row.status}</td>
                      <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => openAction(row, e)}
                          className="rounded border border-slate-800 bg-slate-900 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white hover:bg-slate-800"
                        >
                          Compliance
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {reportReady && !reportQuery.isLoading && totalFiltered > 0 ? (
          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[11px] font-medium tabular-nums text-slate-600">
              Showing{' '}
              <span className="font-semibold text-slate-900">
                {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalFiltered)}
              </span>{' '}
              of <span className="font-semibold text-slate-900">{totalFiltered}</span> (filtered)
            </p>
            <Pagination
              size="small"
              current={page}
              pageSize={pageSize}
              total={totalFiltered}
              showSizeChanger
              pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
              onChange={(p, ps) => {
                const nextSize = ps ?? pageSize;
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(p);
                }
              }}
            />
          </div>
        ) : null}
      </div>
        </>
      ) : null}

      {/* Side panel — indicator-level variance */}
      {reportDataUnlocked && detailRow ? (
        <>
          <div
            className="fixed inset-x-0 bottom-0 top-14 z-[55] bg-slate-900/25 print:hidden"
            aria-hidden
            onClick={() => setDetailRow(null)}
          />
          <aside className="fixed bottom-0 right-0 top-14 z-[60] flex w-full max-w-md flex-col border-l border-slate-300 bg-white shadow-2xl print:hidden">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Variance detail</p>
                <h3 className="text-base font-bold text-slate-900">{detailRow.schoolName}</h3>
              </div>
              <button
                type="button"
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100"
                onClick={() => setDetailRow(null)}
                aria-label="Close panel"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <p className="mb-1 text-xs text-slate-600">
                {detailRow.region} · {detailRow.peerGroup} · Anomaly score{' '}
                <span className="font-mono font-bold tabular-nums">{detailRow.anomalyScore.toFixed(0)}</span>
              </p>
              <p className="mb-3 text-[10px] leading-snug text-slate-500">
                0–100 weighted vs regulatory bands and peer medians: tuition compliance 40%, teacher salary benchmarks
                40%, school size alignment 20%.
              </p>
              <IndicatorDetailTable
                indicators={detailRow.indicators}
                varianceHighlightThreshold={varianceHighlightResolved.value}
              />
            </div>
          </aside>
        </>
      ) : null}

      <Modal
        title={actionRow ? `Compliance — ${actionRow.schoolName}` : 'Compliance'}
        open={Boolean(actionRow)}
        onCancel={closeAction}
        footer={
          <div className="flex justify-end border-t border-slate-100 pt-3">
            <Button type="button" variant="primary" onClick={closeAction}>
              Close
            </Button>
          </div>
        }
        width={720}
        destroyOnClose
      >
        {actionRow && queryYear != null ? (
          <FollowUpsEditor
            scopeKey={`triage-${queryYear}|${actionRow.id}`}
            showInlineAddButton
            complianceCreateScope={{
              kind: 'triage',
              riskTriageQueueItemId: actionRow.id,
              schoolId: actionRow.schoolId ?? '',
              reportingYear: queryYear,
            }}
            contextHint={
              <span>
                Queue row: anomaly score <span className="font-mono font-semibold">{actionRow.anomalyScore.toFixed(0)}</span>
                {' · '}
                {actionRow.primaryAnomaly}
                {' · '}
                Reported status: <span className="font-medium text-slate-800">{actionRow.status}</span>
              </span>
            }
          />
        ) : null}
      </Modal>
    </div>
  );
}
